import React, { useState } from 'react'
import axios from 'axios'
import { test_admin_alert_automation_scenarios_path } from '../../../../routes'
import { ChannelOutcome, ScenarioDraft, TestOutcome } from './types'

interface ScenarioTestPanelProps {
  draft: ScenarioDraft
  onClose: () => void
}

// Why each channel is reported rather than a single "Sent": outside production Twilio refuses to
// deliver and mail is captured rather than sent, and a blanket success message would have a tester
// waiting for a message that was never going to arrive.
const OUTCOME_LABELS: Record<ChannelOutcome, string> = {
  sent: 'sent',
  skipped: 'not sent — delivery is disabled in this environment',
  failed: 'failed — check the logs',
  not_requested: 'not attempted'
}

const ScenarioTestPanel = ({ draft, onClose }: ScenarioTestPanelProps) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [sending, setSending] = useState(false)
  const [outcome, setOutcome] = useState<TestOutcome | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sendTest = () => {
    setSending(true)
    setOutcome(null)
    setError(null)

    axios
      .post(test_admin_alert_automation_scenarios_path(), {
        ...draft,
        test_phone_number: phoneNumber.trim(),
        test_email_address: emailAddress.trim()
      })
      .then(({ data }) => setOutcome(data as TestOutcome))
      .catch((err) => setError(err.response?.data?.error || 'Could not send the test notification.'))
      .finally(() => setSending(false))
  }

  return (
    <div className="border rounded p-3 mt-3 bg-light">
      <div className="fw-bold">Send test notification</div>
      <p className="text-muted small">
        Sent only to the number/email below — not to the scenario’s saved recipients.
      </p>

      <div className="mb-2">
        <label className="visually-hidden" htmlFor="scenario-test-phone">Test phone number</label>
        <input
          id="scenario-test-phone"
          type="text"
          className="form-control"
          placeholder="Test phone number"
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
        />
      </div>

      <div className="mb-2">
        <label className="visually-hidden" htmlFor="scenario-test-email">Test email address</label>
        <input
          id="scenario-test-email"
          type="email"
          className="form-control"
          placeholder="Test email address"
          value={emailAddress}
          onChange={(event) => setEmailAddress(event.target.value)}
        />
      </div>

      {error && <div className="text-danger small mb-2">{error}</div>}

      {outcome && (
        <ul className="small mb-2">
          <li>SMS: {OUTCOME_LABELS[outcome.sms]}</li>
          <li>Email: {OUTCOME_LABELS[outcome.email]}</li>
        </ul>
      )}

      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        <button type="button" className="btn btn-dark" disabled={sending} onClick={sendTest}>
          {sending ? 'Sending…' : 'Send Test'}
        </button>
      </div>
    </div>
  )
}

export default ScenarioTestPanel
